import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Languages, Loader2, Sparkles, Package, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

export const TranslationManager = () => {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState("");
  const [languages, setLanguages] = useState([]);
  const [selected, setSelected] = useState([]); // language codes to warm (excl. en)
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/i18n/warm/status");
      setStatus(r.data);
      if (!r.data.running && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return r.data;
    } catch {
      return null;
    }
  }, []);

  // Load enabled languages (exclude English — it is the source, never translated).
  useEffect(() => {
    api.get("/i18n/languages")
      .then((r) => {
        const langs = (r.data?.languages || []).filter((l) => l.code !== "en");
        setLanguages(langs);
        setSelected(langs.map((l) => l.code)); // default: all selected
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStatus]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(fetchStatus, 2500);
  };

  const toggleLang = (code) =>
    setSelected((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const selectAll = () => setSelected(languages.map((l) => l.code));
  const clearAll = () => setSelected([]);

  const warm = async (scope) => {
    if (!selected.length) {
      toast.error("Select at least one language to translate.");
      return;
    }
    setStarting(scope);
    try {
      const r = await api.post("/admin/i18n/warm", { scope, langs: selected });
      if (r.data.started) {
        toast.success(`Translation started (${scope}) for ${selected.length} language(s). It runs in the background.`);
        startPolling();
        fetchStatus();
      } else {
        toast.message("A translation run is already in progress.");
        startPolling();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start translation");
    } finally {
      setStarting("");
    }
  };

  const running = status?.running;
  const pct = status?.percent || 0;
  const byLang = status?.by_lang || {};

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="translation-manager">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
          <Languages className="h-5 w-5" />
        </span>
        <div>
          <h3 className="text-base font-semibold">Translations</h3>
          <p className="text-sm text-muted-foreground">
            Pre-translate &amp; cache all content so language switching is instant and no tokens are spent on repeat views.
          </p>
        </div>
      </div>

      {/* Language selector */}
      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium">Languages</span>
          <div className="flex gap-3 text-xs">
            <button onClick={selectAll} className="text-primary hover:underline" data-testid="select-all-langs">Select all</button>
            <button onClick={clearAll} className="text-muted-foreground hover:underline" data-testid="clear-all-langs">Clear</button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {languages.map((l) => {
            const on = selected.includes(l.code);
            const lp = byLang[l.code];
            return (
              <button
                key={l.code}
                onClick={() => toggleLang(l.code)}
                disabled={running}
                data-testid={`lang-chip-${l.code}`}
                aria-pressed={on}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                  on
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-muted-foreground hover:border-foreground/30"
                }`}
              >
                {on && <Check className="h-3 w-3" />}
                <span className="uppercase">{l.code}</span>
                <span className="hidden sm:inline opacity-70">{l.name}</span>
                {lp && lp.total > 0 && (
                  <span className="ml-1 font-mono opacity-80">{lp.percent}%</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <Button
          onClick={() => warm("core")}
          disabled={running || starting || !selected.length}
          variant="secondary"
          data-testid="warm-core-button"
          className="justify-start"
        >
          {starting === "core" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          Translate UI, pages &amp; landing (fast)
        </Button>
        <Button
          onClick={() => warm("all")}
          disabled={running || starting || !selected.length}
          data-testid="warm-all-button"
          className="justify-start"
        >
          {starting === "all" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Package className="mr-2 h-4 w-4" />}
          Translate everything incl. products
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        “Everything” translates all products across the selected languages — a large one-time job. It is safe to leave running and safe to re-run (already-translated items are skipped, so a re-run only fills gaps / retries failures).
      </p>

      {status && (status.total > 0 || running) && (
        <div className="mt-6" data-testid="warm-progress">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-medium">
              {running ? "Translating…" : status.finished_at ? "Last run complete" : "Idle"}
            </span>
            <span className="font-mono text-muted-foreground">{status.done}/{status.total} ({pct}%)</span>
          </div>
          <Progress value={pct} className="h-2" />
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {status.current && <span data-testid="warm-current">{status.current}</span>}
            {status.errors > 0 && <Badge variant="secondary">{status.errors} errors</Badge>}
          </div>

          {/* Per-language breakdown */}
          {Object.keys(byLang).length > 0 && (
            <div className="mt-4 grid gap-1.5" data-testid="warm-by-lang">
              {Object.entries(byLang).map(([code, p]) => (
                <div key={code} className="flex items-center gap-3 text-xs">
                  <span className="w-8 font-mono uppercase">{code}</span>
                  <Progress value={p.percent} className="h-1.5 flex-1" />
                  <span className="w-24 text-right font-mono text-muted-foreground">
                    {p.done}/{p.total} ({p.percent}%)
                  </span>
                  {p.errors > 0 && <Badge variant="secondary" className="ml-1">{p.errors} err</Badge>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
